import React, { useState, useRef, useEffect, useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import chunk from 'lodash/chunk';
import clamp from 'lodash/clamp';

import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import CrossIcon from '@atlaskit/icon/core/cross';
import { IconButton } from '@atlaskit/button/new';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Grid, Inline, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useThemeObserver } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import { ExitingPersistence, SlideIn } from '@atlaskit/motion';

import { fg } from '@confluence/feature-gating';

import { HTV2ActionCard } from './HTV2ActionCard.tsx';
import type { OnboardingAction } from './cards/HTV2CardTypes.ts';
import { cards } from './assets/cards/index.ts';
import { useHTV2Actions } from './useHTV2Store.ts';

export function useCarousel<I>(items: I[], pageSize: number) {
	const [idx, setIdx] = useState(0);
	const pages = chunk([...items.keys()], pageSize);

	const effectiveIdx = clamp(idx, 0, items.length - 1);
	const currentPage = pages.findIndex((page) => page.includes(effectiveIdx));

	const hasNext = currentPage < pages.length - 1;
	const hasPrevious = currentPage > 0;

	const nextIdx = pages[currentPage + 1]?.[0];
	const previousIdx = pages[currentPage - 1]?.[0];

	return {
		next: hasNext ? () => setIdx(nextIdx) : undefined,
		previous: hasPrevious ? () => setIdx(previousIdx) : undefined,
		visible: (pages[currentPage] || []).map((i) => items[i]),

		pageKey: effectiveIdx,
	};
}

/**
 * Custom hook that preloads all card images based on the current color mode.
 *
 * This hook uses the `useThemeObserver` to get the current color mode (light or dark)
 * and preloads the appropriate images for each card. It returns an array of `Image` objects
 * with their `src` set to the preloaded image URLs.
 *
 * Images are memo'd to make sure the image objects aren't GC'd by the browser.
 *
 */
function usePreloadCardImages() {
	const { colorMode } = useThemeObserver();

	useMemo(() => {
		const imageSrcs: string[] = Object.values(cards).map(({ light, dark }) =>
			colorMode === 'dark' ? dark : light,
		);
		return imageSrcs.map((src) => {
			const img = new Image();
			img.src = src;
			return img;
		});
	}, [colorMode]);
}

/**
 * Custom hook that checks the homeIsSingleColumn CSS variable.
 *
 * @returns {Object} An object containing:
 * - `ref`: A ref that must be attached to an element that has the
 *   `--homeIsSingleColumn` CSS variable. It should be an element that spans
 *   the width of the home content area, since we will watch its width to
 *   decide when to update.
 * - `isSingleColumn`: A boolean indicating the value of the
 *   `--homeIsSingleColumn` CSS variable (true if '1', false otherwise).
 */
function useHomeIsSingleColumn() {
	const ref = useRef<HTMLElement>(null);
	const [isSingleColumn, setIsSingleColumn] = useState(false);

	useEffect(() => {
		if (!ref.current) {
			return;
		}
		// We watch the carousel for size changes (via the ref), as it's possible that --homeIsSingleColumn
		// can change without a re-render (e.g. if the user resizes the window or the sidebar).
		const resizeObserver = new ResizeObserver(() => {
			setIsSingleColumn(
				Boolean(
					ref.current &&
					window.getComputedStyle(ref.current).getPropertyValue('--homeIsSingleColumn') === '1',
				),
			);
		});
		resizeObserver.observe(ref.current);
		return () => resizeObserver.disconnect();
	}, []);

	return {
		ref,
		isSingleColumn,
	};
}

/**
 * Sorts an array of onboarding actions into buckets based on their state.
 *
 * @param actions - The array of onboarding actions to be sorted.
 * @returns An object where the keys are the states ('pending', 'completed', 'dismissed')
 * and the values are arrays of actions that correspond to each state.
 */
function sortActions(actions: OnboardingAction[]) {
	return actions.reduce<Record<OnboardingAction['state'], OnboardingAction[]>>(
		(buckets, action) => ({
			...buckets,
			[action.state]: [...(buckets[action.state] || []), action],
		}),
		{ pending: [], completed: [], dismissed: [] },
	);
}
const gridStyles = xcss({
	minHeight: '320px',
});
const headingWrapper = xcss({
	minHeight: '24px',
});
const carouselItemsWrapper = xcss({
	overflow: 'hidden',
});

const i18n = defineMessages({
	heading: {
		id: 'experiment-home-onboarding-takeover-v2.carousel.heading',

		defaultMessage: 'Get to know Confluence (Completed {completed} of {total} cards)',
		description:
			'The text is shown above a carousel of onboarding actions. The carousel is full of cards that the user can complete. The placeholder {completed} is replaced with the number of completed cards and {total} is replaced with the total number of cards.',
	},
	previousItem: {
		id: 'experiment-home-onboarding-takeover-v2.carousel.previous-item',
		defaultMessage: 'Show previous item',
		description:
			'The text displayed when hovering over the previous item button above a carousel of onboarding actions.',
	},
	nextItem: {
		id: 'experiment-home-onboarding-takeover-v2.carousel.next-item',
		defaultMessage: 'Show next item',
		description:
			'The text displayed when hovering over the next item button above a carousel of onboarding actions.',
	},
	dismissCarousel: {
		id: 'experiment-home-onboarding-takeover-v2.carousel.dismiss-carousel',
		defaultMessage: 'Dismiss carousel',
		description:
			'The text displayed when hovering over the dismiss carousel button above a carousel of onboarding actions.',
	},
});

export function HTV2Carousel({
	actions,
	dismiss,
	complete,
	onboardingPeriodDay,
	suppressSorting,
	onDismissSection,
}: {
	actions: OnboardingAction[];
	dismiss?: (id: OnboardingAction['id']) => void;
	complete?: (id: OnboardingAction['id']) => void;
	onboardingPeriodDay?: number;
	suppressSorting?: boolean;
	onDismissSection?: () => void;
}) {
	usePreloadCardImages();
	const { formatMessage } = useIntl();
	const { ref, isSingleColumn } = useHomeIsSingleColumn();
	const numVisible = isSingleColumn ? 2 : 3;
	const { pending, completed } = useMemo(() => sortActions(actions), [actions]);
	const cardsToDisplay = suppressSorting ? actions : [...pending, ...completed];
	const { next, previous, visible, pageKey } = useCarousel(cardsToDisplay, numVisible);
	const { getIsXflowTakeoverType } = useHTV2Actions();
	const isXflowTakeoverType = getIsXflowTakeoverType();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Hide the carousel controls if all cards can be shown at once.
	const showControls = pending.length + completed.length > numVisible;
	const [enterFrom, setEnterFrom] = useState<'right' | 'left'>('left');
	const createClickHandler = (callback: (() => void) | undefined, direction: 'left' | 'right') => {
		if (!callback) {
			return;
		}

		return () => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'homeOnboardingTakeoverV2ActionCarouselScroll',
					source: 'onboarding',
					attributes: {
						direction,
						isXflow: isXflowTakeoverType,
					},
				},
			}).fire();
			setEnterFrom(direction);
			setTimeout(callback, 10);
		};
	};

	return (
		<Flex testId="htv2-carousel" ref={ref} direction="column" gap="space.100">
			<Flex gap="space.100" justifyContent="space-between" xcss={headingWrapper}>
				<Heading size="xsmall">
					<FormattedMessage
						{...i18n.heading}
						values={{ completed: completed.length, total: completed.length + pending.length }}
					/>
				</Heading>
				<Inline space="space.100">
					{showControls && (
						<>
							<IconButton
								label={formatMessage(i18n.previousItem)}
								icon={(iconProps) => <ChevronLeftIcon {...iconProps} size="small" />}
								onClick={createClickHandler(previous, 'left')}
								isDisabled={!previous}
								spacing={isXflowTakeoverType ? 'default' : 'compact'}
								appearance={isXflowTakeoverType ? 'default' : 'subtle'}
							/>
							<IconButton
								label={formatMessage(i18n.nextItem)}
								icon={(iconProps) => <ChevronRightIcon {...iconProps} size="small" />}
								onClick={createClickHandler(next, 'right')}
								isDisabled={!next}
								spacing={isXflowTakeoverType ? 'default' : 'compact'}
								appearance={isXflowTakeoverType ? 'default' : 'subtle'}
							/>
						</>
					)}
					{onDismissSection && fg('cc_onboarding_takeover_dismiss_button') && (
						<IconButton
							testId="htv2-carousel-dismiss-button"
							onClick={onDismissSection}
							label={formatMessage(i18n.dismissCarousel)}
							spacing={isXflowTakeoverType ? 'default' : 'compact'}
							appearance={isXflowTakeoverType ? 'default' : 'subtle'}
							icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
						/>
					)}
				</Inline>
			</Flex>
			<Box xcss={carouselItemsWrapper}>
				<ExitingPersistence exitThenEnter appear={false}>
					<SlideIn
						key={pageKey}
						enterFrom={enterFrom}
						exitTo={enterFrom === 'right' ? 'left' : 'right'}
					>
						{(props) => (
							<div {...props}>
								<Grid
									xcss={gridStyles}
									gap="space.200"
									templateColumns="repeat(calc(3 - var(--homeIsSingleColumn, 0)), 1fr)"
								>
									{visible.map((action) => (
										<HTV2ActionCard
											key={action.id}
											action={action}
											dismiss={dismiss}
											complete={complete}
											onboardingPeriodDay={onboardingPeriodDay}
											fadeCardOnComplete={action.fadeCardOnComplete}
											keepCtaOnComplete={action.keepCtaOnCompleted}
										/>
									))}
								</Grid>
							</div>
						)}
					</SlideIn>
				</ExitingPersistence>
			</Box>
		</Flex>
	);
}
